// Web Audio API synthesized sound effects — no audio files needed.
// Uses a module-level singleton AudioContext to avoid browser's 6-context warning.

const MASTER_GAIN = 10;
// Small lookahead so tones scheduled right after resume() don't get clipped.
const LOOKAHEAD = 0.06;

let _ctx: AudioContext | null = null;
let _master: GainNode | null = null;

function ensureCtx(): AudioContext {
  if (!_ctx || _ctx.state === 'closed') {
    _ctx = new AudioContext();
    _master = null;
  }
  if (!_master) {
    _master = _ctx.createGain();
    _master.gain.value = MASTER_GAIN;
    _master.connect(_ctx.destination);
  }
  return _ctx;
}

export function resumeAudio(): void {
  try { ensureCtx(); if (_ctx?.state === 'suspended') void _ctx.resume(); } catch { /* unavailable */ }
}

// Call synchronously inside a user-gesture handler.
// Kicks off resume() without awaiting — tones must use LOOKAHEAD to let it settle.
function resumeCtx(): AudioContext {
  const ctx = ensureCtx();
  if (ctx.state === 'suspended') {
    void ctx.resume();
  }
  return ctx;
}

function playTone(
  ctx: AudioContext,
  frequency: number,
  type: OscillatorType,
  startTime: number,
  duration: number,
  gainPeak: number = 0.3,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(_master!);
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(gainPeak, startTime + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.01);
}

// Soft swoosh — brown noise through a lowpass sweep with a bell-shaped envelope.
// Tweak the variables below to taste.
export function playSwish(): void {
  // ── Tune these ──────────────────────────────────────────────
  const duration      = 0.15;   // total length in seconds
  const freqStart     = 3000;   // lowpass cutoff start (Hz) — higher = brighter
  const freqEnd       = 500;    // lowpass cutoff end (Hz)   — lower = darker
  const filterQ       = 0.5;    // filter resonance (0 = smooth, higher = ringy)
  const peakGain      = 0.005;  // peak volume (0 = silent, 0.05 = loud)
  const attackTime    = 0.1;   // seconds to reach peak
  const sustainUntil  = 0.2;   // seconds to hold peak before fade
  // ────────────────────────────────────────────────────────────

  try {
    const ctx = resumeCtx();
    const t = ctx.currentTime + LOOKAHEAD;

    const bufferSize = Math.ceil(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 18;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(freqStart, t);
    filter.frequency.exponentialRampToValueAtTime(freqEnd, t + duration);
    filter.Q.value = filterQ;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(peakGain, t + attackTime);
    gain.gain.setValueAtTime(peakGain, t + sustainUntil);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(_master!);
    source.start(t);
    source.stop(t + duration + 0.01);
  } catch { /* AudioContext unavailable */ }
}

// Module-level so non-hook code (e.g. mobileInit click handler, Button pointerdown)
// can play the half-tones without going through a React hook.
export function playTapDown(): void {
  try {
    const ctx = resumeCtx();
    const t = ctx.currentTime + LOOKAHEAD;
    playTone(ctx, 220, 'sine', t, 0.03, 0.12);
  } catch { /* AudioContext unavailable */ }
}

export function playTapUp(): void {
  try {
    const ctx = resumeCtx();
    const t = ctx.currentTime + LOOKAHEAD;
    playTone(ctx, 330, 'sine', t, 0.025, 0.08);
  } catch { /* AudioContext unavailable */ }
}

// Short mechanical tick — suitable for playing once per second in a countdown.
// Uses bandpass-filtered white noise, the standard technique for clock/metronome ticks.
export function playClockTick(): void {
  try {
    const ctx = resumeCtx();
    const t = ctx.currentTime + LOOKAHEAD;

    // 30 ms white noise burst
    const bufferSize = Math.ceil(ctx.sampleRate * 0.03);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    // Bandpass centred at 1 800 Hz — warm but crisp, not piercing
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1000;
    filter.Q.value = 1.5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.05, t + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.028);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(_master!);
    source.start(t);
    source.stop(t + 0.03);
  } catch { /* AudioContext unavailable */ }
}

export interface SoundFunctions {
  tap: () => void;
  tapDown: () => void;
  tapUp: () => void;
  correct: () => void;
  incorrect: () => void;
  drag: (intensity?: number) => void;
  complete: () => void;
  clockTick: () => void;
}

export function useSounds(): SoundFunctions {
  const tap = () => {
    try {
      const ctx = resumeCtx();
      const t = ctx.currentTime + LOOKAHEAD;
      playTone(ctx, 220, 'sine', t, 0.03, 0.12);
      playTone(ctx, 330, 'sine', t + 0.05, 0.025, 0.08);
    } catch { /* AudioContext unavailable */ }
  };

  const correct = () => {
    try {
      const ctx = resumeCtx();
      const t = ctx.currentTime + LOOKAHEAD;
      playTone(ctx, 523.25, 'sine', t, 0.1, 0.05);        // C5
      playTone(ctx, 659.25, 'sine', t + 0.1, 0.1, 0.05);  // E5
      playTone(ctx, 783.99, 'sine', t + 0.2, 0.15, 0.05); // G5
    } catch { /* AudioContext unavailable */ }
  };

  const incorrect = () => {
    try {
      const ctx = resumeCtx();
      playTone(ctx, 50, 'square', ctx.currentTime + LOOKAHEAD, 0.4, 0.02);
    } catch { /* AudioContext unavailable */ }
  };

  // intensity: 0..1 — varies pitch very slightly around the tap-up tone.
  const drag = (intensity: number = 0) => {
    try {
      const ctx = resumeCtx();
      const clamped = Math.max(0, Math.min(1, intensity));
      // 322–338 Hz — same character as tap-up (330 Hz), barely perceptible drift
      const freq = 322 + clamped * 16;
      playTone(ctx, freq, 'sine', ctx.currentTime + LOOKAHEAD, 0.025, 0.06);
    } catch { /* AudioContext unavailable */ }
  };

  const complete = () => {
    try {
      const ctx = resumeCtx();
      const t = ctx.currentTime + LOOKAHEAD;
      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
      notes.forEach((freq, i) => {
        const isLast = i === notes.length - 1;
        playTone(ctx, freq, 'sine', t + i * 0.12, isLast ? 0.4 : 0.1, 0.05);
      });
    } catch { /* AudioContext unavailable */ }
  };

  return { tap, tapDown: playTapDown, tapUp: playTapUp, correct, incorrect, drag, complete, clockTick: playClockTick };
}
